'use client';
import React, {
  Fragment,
  useEffect,
  useContext,
  useState,
} from 'react';
import { useActor } from '@xstate/react';
import { GlobalStateContext } from '../state/app/index';
import classNames from 'classnames';
import { useHotkeys } from 'react-hotkeys-hook';
import { getHotkeysByRegion } from '../dryKeyboardMapping';
import DryChrome from './DryChromeClass';
import Header from './components/Header';
import Palette from './Palette';
import NoteSearchPalette from './NoteSearchPalette';
import track from '../utils/trackers';
import { getOwnerID } from '../utils/DryCookies';
import { startAuthHeartbeat, stopAuthHeartbeat } from '../utils/firebaseAuth';
import {
  vhFixWithResize,
  controlOnScreenKeyboard,
  isDesktop,
  isMobile,
} from '../utils/helpers.ts';
import { globalKeys, handleGlobalShortcuts } from '../dryKeyboardMapping';
import ErrorBoundary from './ErrorBoundary';
import AppNavigation from './components/AppNavigation';
import { preloadCriticalComponents } from '../utils/prefetchUtils';

import MobileActionsMenu from './components/MobileActionsMenu';
import OpenNotesPanel from './editor/OpenNotesPanel';
import UpdateToast from './components/UpdateToast';
import { useTheme, applyTheme } from '../hooks/useTheme';
import { THEMES, THEME_STORAGE_KEY } from '../utils/themes';

function ThemeSync() {
  const globalServices = useContext(GlobalStateContext);
  const [appState] = useActor(globalServices.appState);
  const cloudTheme = appState.context.userSettings?.theme;

  useEffect(() => {
    if (cloudTheme && THEMES[cloudTheme]) {
      localStorage.setItem(THEME_STORAGE_KEY, cloudTheme);
      applyTheme(cloudTheme);
    }
  }, [cloudTheme]);

  return null;
}

const Drynotes = () => {
  const globalServices = useContext(GlobalStateContext);
  const [isAppNavExpanded, setIsAppNavExpanded] = useState(false); // Start collapsed (icons only)
  useTheme(); // Apply persisted theme on mount

  // Guard against undefined context (e.g., during SSR or before provider mounts)
  if (
    !globalServices ||
    !globalServices.appState ||
    !globalServices.paletteState ||
    !globalServices.modalState
  ) {
    return null;
  }

  const [appState, sendAppState] = useActor(globalServices.appState);
  const [paletteState, sendPaletteState] = useActor(
    globalServices.paletteState
  );
  const {
    context: { applicationTitle, hasNotes, breakpoint, showOpenNotesPanel },
  } = appState;

  const globalHotkeys = React.useMemo(() => getHotkeysByRegion(
    'GLOBAL',
    sendAppState,
    sendPaletteState
  ), [sendAppState, sendPaletteState]);

  globalHotkeys.forEach(({ keys, callback }) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useHotkeys(keys, callback, { preventDefault: true, enableOnFormTags: true }, [callback]);
  });

  useEffect(() => {
    track.screenview('main');
    // Preload critical components in the background for instant access
    preloadCriticalComponents();
  }, []);

  useEffect(() => {
    const ownerId = getOwnerID();
    vhFixWithResize();
    startAuthHeartbeat();
    controlOnScreenKeyboard();

    if (
      breakpoint === 'mobile' ||
      breakpoint === 'mobileLandscape' ||
      breakpoint === 'tablet' ||
      breakpoint === 'tabletLandscape'
    ) {
      sendAppState({
        type: 'togglePanelVisability',
      });
    }

    // Cleanup: stop heartbeat when component unmounts
    return () => {
      stopAuthHeartbeat();
    };
  }, [sendAppState, breakpoint]);

  useEffect(() => {
    if (applicationTitle) {
      document.title = applicationTitle;
    }
  }, [applicationTitle]);

  const containerClass = classNames({
    'home-container-large': isDesktop(breakpoint) && hasNotes,
    'home-container-large-new-user': isDesktop(breakpoint) && !hasNotes,
    'home-container-small': isMobile(breakpoint),
    'app-nav-expanded': isDesktop(breakpoint) && isAppNavExpanded,
    'app-nav-collapsed': isDesktop(breakpoint) && !isAppNavExpanded,
  });

  return (
    <Fragment>
      <ThemeSync />
      <div className={containerClass}>
        <Header />
        <AppNavigation
          isExpanded={isAppNavExpanded}
          onToggle={() => setIsAppNavExpanded((prev) => !prev)}
          isVisible={isDesktop(breakpoint)}
        />
        <DryChrome />
        <MobileActionsMenu />
        <UpdateToast />
        <ErrorBoundary>
          <Palette />
          <NoteSearchPalette />
        </ErrorBoundary>
      </div>
      {isMobile(breakpoint) && (
        <div className={`open-notes-drawer${showOpenNotesPanel ? ' open' : ''}`}>
          <OpenNotesPanel />
        </div>
      )}
    </Fragment>
  );
};

export default Drynotes;
